import React, { useEffect, useState } from 'react';
import ModalPortal from './ModalPortal';

function StatsModal({ isOpen, onClose, backendUrl, authToken, authUser }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!authToken) {
      setResults([]);
      setError('You must be logged in to view statistics.');
      setLoading(false);
      return;
    }
    const base = (backendUrl || '');
    setLoading(true);
    setError('');
    fetch(`${base}/api/solo/results`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success && Array.isArray(data.results)) {
          setResults(data.results);
        } else {
          setResults([]);
          setError(data && data.error ? data.error : 'Failed to load statistics.');
        }
      })
      .catch(() => {
        setResults([]);
        setError('Failed to load statistics.');
      })
      .finally(() => setLoading(false));
  }, [isOpen, backendUrl, authToken]);

  if (!isOpen) return null;

  const games = Array.isArray(results) ? results : [];
  const gamesWithTime = games.map((g) => {
    const t = g && g.played_at ? new Date(g.played_at).getTime() : Number.NaN;
    return { ...g, _time: Number.isNaN(t) ? null : t };
  });
  const chartGames = gamesWithTime
    .filter((g) => g._time !== null)
    .sort((a, b) => a._time - b._time);

  const bestScore = games.reduce(
    (m, g) => (typeof g.score === 'number' && g.score > m ? g.score : m),
    0
  );

  // Larger modal & graph
  const width = 900;
  const height = 360;
  const marginLeft = 56;
  const marginRight = 32;
  const marginTop = 28;
  const marginBottom = 60;
  const innerW = width - marginLeft - marginRight;
  const innerH = height - marginTop - marginBottom;

  const chartPoints = [];
  const bestLinePoints = [];
  const xTicks = [];

  if (chartGames.length > 0 && innerW > 0 && innerH > 0) {
    const maxScore = bestScore || 1;

    // Build unique calendar-day buckets (YYYY-MM-DD) so all games on the same
    // day share the same X position.
    const dayMap = new Map(); // key -> { key, time }
    chartGames.forEach((g) => {
      const d = new Date(g._time);
      if (Number.isNaN(d.getTime())) return;
      const key = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');
      if (!dayMap.has(key)) {
        dayMap.set(key, { key, time: g._time });
      }
    });

    const dayEntries = Array.from(dayMap.values()).sort((a, b) => a.time - b.time);
    const dayCount = dayEntries.length;

    const dayIndexByKey = new Map();
    dayEntries.forEach((entry, idx) => {
      dayIndexByKey.set(entry.key, idx);
    });

    const firstDay = dayEntries[0] ? new Date(dayEntries[0].time) : null;
    const lastDay =
      dayEntries[dayEntries.length - 1] ? new Date(dayEntries[dayEntries.length - 1].time) : null;
    const dayMs = 1000 * 60 * 60 * 24;
    const spanDays =
      firstDay && lastDay ? Math.max(0, (lastDay - firstDay) / dayMs) : 0;

    let runningBest = 0;
    chartGames.forEach((g) => {
      const d = new Date(g._time);
      if (Number.isNaN(d.getTime())) return;
      const key = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');
      const dayIndex = dayIndexByKey.get(key);
      if (dayIndex == null) return;

      const ratio = dayCount === 1 ? 0.5 : dayIndex / (dayCount - 1);
      const s = typeof g.score === 'number' ? g.score : 0;
      const x = marginLeft + ratio * innerW;
      const y = marginTop + ((maxScore - s) / maxScore) * innerH;

	      chartPoints.push({ x, y, score: s, played_at: g.played_at });
      runningBest = Math.max(runningBest, s);
      const by = marginTop + ((maxScore - runningBest) / maxScore) * innerH;
      bestLinePoints.push(`${x},${by}`);
    });

    // Generate up to 6 X-axis ticks aligned to day buckets
    const maxTicks = 6;
    const tickSlots = Math.min(dayCount, maxTicks);
    const usedIdx = new Set();
    for (let i = 0; i < tickSlots; i++) {
      const ratio = tickSlots === 1 ? 0.5 : i / (tickSlots - 1);
      const approxIdx =
        tickSlots === 1 ? 0 : Math.round((dayCount - 1) * ratio);
      const idx = Math.min(dayCount - 1, Math.max(0, approxIdx));
      if (usedIdx.has(idx)) continue;
      usedIdx.add(idx);
      const entry = dayEntries[idx]; // eslint-disable-line security/detect-object-injection -- array access with clamped numeric index
      const d = new Date(entry.time);
      if (Number.isNaN(d.getTime())) continue;
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year2 = String(d.getFullYear()).slice(-2);
      const label = spanDays > 180 ? `${month}/${year2}` : `${month}/${day}`;

      const xRatio = dayCount === 1 ? 0.5 : idx / (dayCount - 1);
      const x = marginLeft + xRatio * innerW;
      xTicks.push({ x, label });
    }
  }

  // Tooltip positioning (keep tooltip inside the chart area and avoid clipping)
  let tooltipX = null;
  let tooltipY = null;
  if (hoveredPoint) {
    const halfWidth = 60; // matches tooltip rect half-width
    const minX = marginLeft + halfWidth;
    const maxX = marginLeft + innerW - halfWidth;
    let x = hoveredPoint.x;
    if (x < minX) x = minX;
    if (x > maxX) x = maxX;

    // Prefer showing tooltip above point; if too close to top, show below.
    let y = hoveredPoint.y - 28;
    const minTopY = marginTop + 20;
    if (y < minTopY) {
      y = hoveredPoint.y + 28;
      const maxBottomY = marginTop + innerH - 20;
      if (y > maxBottomY) y = maxBottomY;
    }

    tooltipX = x;
    tooltipY = y;
  }

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch (e) {
      return '';
    }
  };

  return (
    <ModalPortal>
      <div className="settings-modal-overlay" onClick={onClose}>
        <div
          className="settings-modal"
          onClick={(e) => e.stopPropagation()}
          style={{ width, maxWidth: '92%' }}
        >
	          <div className="settings-modal-header">
	            <h3>Statistics - Solo Mode</h3>
            <button className="icon-button" onClick={onClose} title="Close">✖</button>
          </div>
          <div className="settings-modal-body">
            {loading && <div>Loading statistics...</div>}
            {!loading && error && (
              <div style={{ color: '#f88', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{error}</div>
            )}
            {!loading && !error && games.length === 0 && (
              <div style={{ fontSize: '0.9rem' }}>
                No solo games recorded yet. Play some solo mode games while logged in to build your statistics.
              </div>
            )}

            {!loading && !error && games.length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: '100%', maxWidth: width }}>
                    <svg
                      width="100%"
                      height={height}
                      viewBox={`0 0 ${width} ${height}`}
                    >
                      <rect
                        x={marginLeft}
                        y={marginTop}
                        width={innerW}
                        height={innerH}
                        fill="#050711"
                        stroke="#22263a"
                      />
                      {/* Y-axis labels */}
                      <text
                        x={marginLeft - 8}
                        y={marginTop + 10}
                        textAnchor="end"
                        fill="#adb3d3"
                        fontSize="10"
                      >
                        {bestScore}
                      </text>
                      <text
                        x={marginLeft - 8}
                        y={marginTop + innerH}
                        textAnchor="end"
                        fill="#6b708c"
                        fontSize="10"
                      >
                        0
                      </text>
                      {/* X-axis & time labels */}
                      <line
                        x1={marginLeft}
                        y1={marginTop + innerH}
                        x2={marginLeft + innerW}
                        y2={marginTop + innerH}
                        stroke="#3b3f58"
                        strokeWidth="1"
                      />
                      {xTicks.map((tick, idx) => (
                        <g key={idx}>
                          <line
                            x1={tick.x}
                            y1={marginTop + innerH}
                            x2={tick.x}
                            y2={marginTop + innerH + 4}
                            stroke="#3b3f58"
                            strokeWidth="1"
                          />
                          <text
                            x={tick.x}
                            y={marginTop + innerH + 16}
                            textAnchor="middle"
                            fill="#6b708c"
                            fontSize="10"
                          >
                            {tick.label}
                          </text>
                        </g>
                      ))}
                      {/* Best score running line */}
                      {bestLinePoints.length > 1 && (
                        <polyline
                          points={bestLinePoints.join(' ')}
                          fill="none"
                          stroke="#ffa94d"
                          strokeWidth="2"
                        />
                      )}
                      {/* Score points and polyline */}
                      {/* Show points (running-best orange line kept) */}
                      {chartPoints.map((p, idx) => (
                        <circle
                          key={idx}
                          cx={p.x}
                          cy={p.y}
                          r={3}
                          fill="#4dabf7"
                          stroke="#0b1020"
                          strokeWidth="1"
                          onMouseEnter={() => setHoveredPoint(p)}
                          onMouseLeave={() => setHoveredPoint(null)}
                        />
                      ))}
                      {hoveredPoint && tooltipX != null && tooltipY != null && (
                        <g transform={`translate(${tooltipX}, ${tooltipY})`}>
                          <rect
                            x={-60}
                            y={-20}
                            width={120}
                            height={28}
                            rx={4}
                            ry={4}
                            fill="#111827"
                            stroke="#4b5563"
                            strokeWidth="1"
                          />
                          <text
                            x={0}
                            y={-4}
                            textAnchor="middle"
                            fill="#e5e7eb"
                            fontSize="10"
                          >
                            {hoveredPoint.score} on {formatDate(hoveredPoint.played_at)}
                          </text>
                        </g>
                      )}
                    </svg>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '0.75rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1.5rem',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                  }}
                >
                  <div style={{ minWidth: '180px' }}>
                    <div><strong>User:</strong> {authUser && authUser.username}</div>
                    <div><strong>Games played:</strong> {games.length}</div>
                    <div><strong>Best score:</strong> {bestScore || '—'}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '200px', color: '#9aa0c2' }}>
                    Blue points: individual solo game scores over time. Orange line: your running personal best.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default StatsModal;

